import { Button } from "../components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card"
import { Input } from "../components/ui/input"
import { Label } from "../components/ui/label"
import { useState } from "react"
import { supabase } from "../supabaseClient"
import { useNavigate } from "react-router-dom"

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSignupMode, setIsSignupMode] = useState(false)
  const navigate = useNavigate()

  const handleLogin = async () => {
    setError('')
    setIsLoading(true)
    
    // Resolve email - if input doesn't contain @, treat as username
    let loginEmail = email
    if (!email.includes('@')) {
      // Look up email by username (display_name)
      const { data: profile } = await supabase
        .from('profiles')
        .select('email')
        .ilike('display_name', email.trim())
        .maybeSingle()
      
      if (!profile?.email) {
        setError('Username not found')
        setIsLoading(false)
        return
      }
      loginEmail = profile.email
    }
    
    const { data, error } = await supabase.auth.signInWithPassword({ email: loginEmail, password })

    setIsLoading(false)

    if (error) {
      setError(error.message)
    } else if (data.user) {
      navigate('/dashboard')
    }
  }

  const handleSignup = async () => {
    setError('')
    
    if (!displayName.trim()) {
      setError('Please enter a display name')
      return
    }
    
    setIsLoading(true)
    
    // Check if display name is already taken
    const { data: existingProfile } = await supabase
      .from('profiles')
      .select('id')
      .ilike('display_name', displayName.trim())
      .maybeSingle()
    
    if (existingProfile) {
      setError('This username is already taken. Please choose another.')
      setIsLoading(false)
      return
    }
    
    const { data, error } = await supabase.auth.signUp({ 
      email, 
      password,
      options: {
        data: {
          display_name: displayName.trim()
        }
      }
    })

    setIsLoading(false)

    if (error) {
      setError(error.message)
    } else if (data.user) {
      // Update the profile with display name
      await supabase
        .from('profiles')
        .upsert({ 
          id: data.user.id, 
          display_name: displayName.trim(),
          email: email 
        })
      
      setError('Check your email for the confirmation link!')
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FDFBF7] to-[#f0ebe3] flex items-center justify-center p-4">
      {/* Main card */}
      <Card className="w-full max-w-md shadow-xl border-0 bg-white animate-fade-in">
        <CardHeader className="space-y-4 pb-4 text-center">
          {/* Logo */}
          <div className="flex flex-col items-center space-y-3">
            <div className="w-16 h-16 rounded-full bg-[#1e3a5f] flex items-center justify-center">
              <svg width="32" height="32" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="50" cy="50" r="40" stroke="#F15A22" strokeWidth="5" />
                <path d="M50 10 Q50 50 50 90" stroke="#F15A22" strokeWidth="4" />
                <path d="M10 50 Q50 50 90 50" stroke="#F15A22" strokeWidth="4" />
              </svg>
            </div>
            <div>
              <CardTitle className="text-3xl font-bold text-[#1e3a5f] tracking-wide logo-text">
                WRIGHT PICKS
              </CardTitle>
              <CardDescription className="text-base text-gray-500 mt-1">
                Wright Picks
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-5">
          {/* Error/Success message */}
          {error && (
            <div className={`${error.includes('Check your email') 
              ? 'bg-green-50 border-green-200 text-green-700' 
              : 'bg-red-50 border-red-200 text-red-700'} border rounded-lg p-3 text-center text-sm`}>
              {error}
            </div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); isSignupMode ? handleSignup() : handleLogin(); }}>
            {/* Display Name field (only for signup) */}
            {isSignupMode && (
              <div className="space-y-2 mb-5">
                <Label htmlFor="displayName" className="text-gray-700 text-sm font-medium">
                  Display Name
                </Label>
                <Input 
                  id="displayName" 
                  type="text" 
                  placeholder="Your username for the leaderboard" 
                  className="h-11 border-gray-300 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]" 
                  value={displayName} 
                  onChange={(e) => setDisplayName(e.target.value)} 
                />
              </div>
            )}
            
            {/* Email field (or Email/Username for login) */}
            <div className="space-y-2 mb-5">
              <Label htmlFor="email" className="text-gray-700 text-sm font-medium">
                {isSignupMode ? 'Email' : 'Email or Username'}
              </Label>
              <Input 
                id="email" 
                type={isSignupMode ? 'email' : 'text'} 
                placeholder={isSignupMode ? 'you@example.com' : 'Email or username'} 
                className="h-11 border-gray-300 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
              />
            </div>
            
            {/* Password field */}
            <div className="space-y-2 mb-5">
              <Label htmlFor="password" className="text-gray-700 text-sm font-medium">
                Password
              </Label>
              <Input 
                id="password" 
                type="password" 
                placeholder="••••••••" 
                className="h-11 border-gray-300 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
              />
            </div>
            
            {/* Buttons */}
            <div className="space-y-3 pt-2">
              {isSignupMode ? (
                <>
                  <Button 
                    type="submit"
                    className="w-full h-12 text-base font-semibold gradient-accent gradient-accent-hover text-white transition-all shadow-md hover:shadow-lg" 
                    disabled={isLoading}
                  >
                    {isLoading ? 'Creating Account...' : 'Create Account'}
                  </Button>
                  <Button 
                    type="button"
                    variant="outline" 
                    className="w-full h-11 text-base font-medium border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors" 
                    onClick={() => setIsSignupMode(false)}
                    disabled={isLoading}
                  >
                    Back to Login
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    type="submit"
                    className="w-full h-12 text-base font-semibold gradient-brand text-white transition-all shadow-md hover:shadow-lg" 
                    disabled={isLoading}
                  >
                    {isLoading ? 'Loading...' : 'Log In'}
                  </Button>
                  <Button 
                    type="button"
                    variant="outline" 
                    className="w-full h-11 text-base font-medium border-[#F15A22] text-[#F15A22] hover:bg-[#F15A22] hover:text-white transition-colors" 
                    onClick={() => setIsSignupMode(true)}
                    disabled={isLoading}
                  >
                    Create Account
                  </Button>
                </>
              )}
            </div>
          </form>
          
          {/* Forgot password */}
          {!isSignupMode && (
            <div className="text-center pt-2">
              <button type="button" className="text-sm text-gray-500 hover:text-[#F15A22] transition-colors">
                Forgot your password?
              </button>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Footer tagline */}
      <div className="absolute bottom-6 left-0 right-0 text-center text-gray-400 text-sm">
        Pick 16 Teams • Win Big • Glory Awaits
      </div>
    </div>
  )
}
